import React from 'react';
import { Icon } from '../icons.jsx';
import {
  Avatar, StatusChip, FormattedText, ChatBlock, LizziAvatar,
  ChatComposer, bidTitle, fmtMoney,
} from '../components.jsx';
import { notify } from '../modals.jsx';
import { useData } from '../data/DataProvider.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  listThreads, createThread, loadThreadMessages, sendChatMessage,
  togglePinThread, deleteThread, listBidDocuments, loadBidMemory,
  refreshBidInStore,
} from '../data/repo.js';

const { useState, useEffect, useRef, useMemo, useCallback } = React;

// ===============================================
// Chat con Lizzi — enrutador demo / real
// ===============================================
function ChatScreen() {
  let demoMode = false;
  try { demoMode = useData().demoMode; } catch (e) { demoMode = false; }
  return demoMode ? <DemoChat /> : <RealChat />;
}

// ===============================================
// REAL — conversaciones persistidas en Supabase
// ===============================================
function RealChat() {
  const { user } = useAuth();
  const BIDS = window.MockData?.BIDS || [];
  const CURRENT_USER = window.MockData?.CURRENT_USER || { name: 'Tú' };

  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [query, setQuery] = useState('');
  const [picker, setPicker] = useState(false);
  const [docs, setDocs] = useState([]);
  const [memory, setMemory] = useState([]);
  const [showCtx, setShowCtx] = useState(false); // panel de contexto en pantallas estrechas

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const activeThread = useMemo(() => threads.find((t) => t.id === activeId) || null, [threads, activeId]);
  const activeBid = useMemo(
    () => (activeThread?.bid_id ? BIDS.find((b) => b.id === activeThread.bid_id) : null),
    [activeThread, BIDS],
  );

  const focusInput = useCallback(() => { setTimeout(() => inputRef.current?.focus(), 30); }, []);

  const refreshThreads = useCallback(async () => {
    const t = await listThreads();
    setThreads(t);
    return t;
  }, []);

  // Carga inicial de hilos
  useEffect(() => {
    let active = true;
    setLoadingThreads(true);
    refreshThreads()
      .then((t) => {
        if (!active) return;
        // Conversación pedida desde el panel lateral (⌘J → "Abrir en Chat").
        let wanted = null;
        try { wanted = sessionStorage.getItem('lizzi:open-thread'); sessionStorage.removeItem('lizzi:open-thread'); } catch (_) {}
        setActiveId((prev) => (wanted && t.some((x) => x.id === wanted) ? wanted : (prev || t[0]?.id || null)));
      })
      .catch((e) => notify(e.message || 'No se pudieron cargar las conversaciones', 'X'))
      .finally(() => { if (active) setLoadingThreads(false); });
    return () => { active = false; };
  }, [refreshThreads]);

  // Selección de hilo cuando ya estamos en /chat y se pide abrir otro.
  useEffect(() => {
    const onSelect = (e) => {
      if (!e.detail) return;
      try { sessionStorage.removeItem('lizzi:open-thread'); } catch (_) {}
      refreshThreads().then(() => setActiveId(e.detail)).catch(() => {});
    };
    window.addEventListener('lizzi:select-thread', onSelect);
    return () => window.removeEventListener('lizzi:select-thread', onSelect);
  }, [refreshThreads]);

  const goToBid = (id) => { if (id) window.dispatchEvent(new CustomEvent('lizzi:open-bid', { detail: id })); };

  // Carga de mensajes + contexto al cambiar de hilo
  useEffect(() => {
    if (!activeId) { setMessages([]); setDocs([]); setMemory([]); return; }
    let active = true;
    setLoadingMsgs(true);
    loadThreadMessages(activeId)
      .then((m) => { if (active) setMessages(m); })
      .catch(() => { if (active) setMessages([]); })
      .finally(() => { if (active) { setLoadingMsgs(false); focusInput(); } });
    return () => { active = false; };
  }, [activeId, focusInput]);

  // Contexto lateral (documentos + memoria) del bid asociado
  useEffect(() => {
    const bidId = activeThread?.bid_id;
    if (!bidId) { setDocs([]); setMemory([]); return; }
    let active = true;
    Promise.all([listBidDocuments(bidId), loadBidMemory(bidId)])
      .then(([d, m]) => { if (active) { setDocs(d); setMemory(m); } })
      .catch(() => {});
    return () => { active = false; };
  }, [activeThread?.bid_id, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const handleNewThread = async (bid) => {
    try {
      const title = bid ? `Sobre ${bidTitle(bid)}`.slice(0, 60) : 'Nueva conversación';
      const t = await createThread({ bidId: bid?.id || null, title }, user?.id);
      await refreshThreads();
      setActiveId(t.id);
      setPicker(false);
      focusInput();
    } catch (e) {
      notify(e.message || 'No se pudo crear la conversación', 'X');
    }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending || !activeId) return;
    setDraft('');
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await sendChatMessage(activeId, text);
      // Recarga desde BD (fuente de verdad). Si por cualquier motivo no
      // devuelve la respuesta de Lizzi, usamos la `reply` de la función
      // para no dejar al usuario sin respuesta visible.
      let fresh = [];
      try { fresh = await loadThreadMessages(activeId); } catch (_) { fresh = []; }
      const hasReply = fresh.some((m) => m.role !== 'user');
      if (fresh.length && hasReply) {
        setMessages(fresh);
      } else if (res?.reply) {
        setMessages((m) => [
          ...m.filter((x) => !String(x.id).startsWith('tmp-')),
          { id: `u-${Date.now()}`, role: 'user', content: text },
          { id: `a-${Date.now()}`, role: 'lizzi', content: res.reply },
        ]);
      } else if (fresh.length) {
        setMessages(fresh);
      }
      refreshThreads();
      // Si Lizzi modificó la licitación o su ficha, refresca el bid en el
      // store global para que el cambio se vea al ir a la licitación, sin
      // recargar la página ni remontar el chat.
      const acts = res?.actions || [];
      const changedBid = acts.some((a) => a.type === 'bid_updated' || a.type === 'analysis_updated');
      const bidId = activeThread?.bid_id;
      if (changedBid && bidId) await refreshBidInStore(bidId);
    } catch (e) {
      const msg = e?.message || 'Lizzi no pudo responder';
      notify(msg, 'X');
      // Mantén el mensaje del usuario y muestra el error como respuesta de Lizzi.
      setMessages((m) => [
        ...m.filter((x) => !String(x.id).startsWith('tmp-')),
        { id: `u-${Date.now()}`, role: 'user', content: text },
        { id: `err-${Date.now()}`, role: 'lizzi', content: `⚠️ No pude responder: ${msg}` },
      ]);
    } finally {
      setSending(false);
      focusInput();
    }
  };

  const handlePin = async () => {
    if (!activeThread) return;
    try {
      await togglePinThread(activeThread.id, !activeThread.pinned);
      await refreshThreads();
    } catch (e) { notify(e.message, 'X'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteThread(id);
      const t = await refreshThreads();
      if (activeId === id) setActiveId(t[0]?.id || null);
    } catch (e) { notify(e.message, 'X'); }
  };

  const filtered = threads.filter((t) =>
    !query.trim() || (t.title || '').toLowerCase().includes(query.toLowerCase()));
  const pinned = filtered.filter((t) => t.pinned);
  const recent = filtered.filter((t) => !t.pinned);

  return (
    <div className="chat-wrap fade-in">
    <div className="chat-layout">
      {/* Lista de hilos */}
      <div className="chat-threads" style={{ borderRight: '1px solid var(--line-1)', background: 'var(--bg-1)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="page-eyebrow" style={{ margin: 0 }}>Conversaciones</span>
            <button className="btn btn-ghost btn-icon" title="Nueva conversación" onClick={() => setPicker(true)}><Icon.Plus size={14} /></button>
          </div>
        </div>
        <div style={{ padding: '0 12px 8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid var(--line-2)', borderRadius: 8, background: 'var(--bg-2)' }}>
            <Icon.Search size={13} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar en chats…" style={{ background: 'transparent', border: 0, fontSize: 12, flex: 1, color: 'var(--text-1)', outline: 'none' }} />
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '4px 8px 10px' }}>
          {loadingThreads && <div style={{ padding: 12, fontSize: 12, color: 'var(--text-4)' }}>Cargando…</div>}
          {!loadingThreads && filtered.length === 0 && (
            <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--text-4)', fontSize: 12.5 }}>
              No hay conversaciones todavía.
              <div style={{ marginTop: 10 }}>
                <button className="btn btn-outline" style={{ fontSize: 12 }} onClick={() => setPicker(true)}><Icon.Plus size={13} /> Nueva</button>
              </div>
            </div>
          )}
          {pinned.length > 0 && <div className="section-title" style={{ padding: '8px 8px 4px' }}>Fijados</div>}
          {pinned.map((t) => <ThreadRow key={t.id} thread={t} bids={BIDS} active={activeId === t.id} onClick={() => setActiveId(t.id)} onDelete={() => handleDelete(t.id)} />)}
          {recent.length > 0 && <div className="section-title" style={{ padding: '14px 8px 4px' }}>Recientes</div>}
          {recent.map((t) => <ThreadRow key={t.id} thread={t} bids={BIDS} active={activeId === t.id} onClick={() => setActiveId(t.id)} onDelete={() => handleDelete(t.id)} />)}
        </div>
      </div>

      {/* Conversación */}
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-0)' }}>
        {!activeId ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, color: 'var(--text-3)' }}>
            <LizziAvatar size={64} />
            <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--text-1)' }}>Habla con Lizzi</div>
            <div style={{ fontSize: 13.5, maxWidth: 360, textAlign: 'center', lineHeight: 1.6 }}>Crea una conversación asociada a una licitación. Lizzi usará su ficha, sus pliegos y la memoria activa como contexto.</div>
            <button className="btn btn-primary" onClick={() => setPicker(true)}><Icon.Plus size={15} /> Nueva conversación</button>
          </div>
        ) : (
          <>
            <div style={{ padding: '14px 22px', borderBottom: '1px solid var(--line-1)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <LizziAvatar size={40} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activeThread?.title || 'Conversación'}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                  {activeBid ? `Contexto: ${activeBid.entityShort || activeBid.entity || bidTitle(activeBid)}` : 'Sin licitación asociada'} · {messages.length} mensajes
                </div>
              </div>
              <button className="btn btn-ghost btn-icon chat-ctx-toggle" title="Contexto de la conversación" onClick={() => setShowCtx((v) => !v)}><Icon.PanelLeft size={16} style={{ transform: 'scaleX(-1)' }} /></button>
              {activeBid && (
                <button className="btn btn-outline chat-bid-link" style={{ fontSize: 12.5, flexShrink: 0 }} title={bidTitle(activeBid)} onClick={() => goToBid(activeBid.id)}>
                  <Icon.Briefcase size={13} /> Ver licitación <Icon.ArrowUpRight size={12} />
                </button>
              )}
              <button className="btn btn-ghost btn-icon" title={activeThread?.pinned ? 'Quitar fijado' : 'Fijar'} onClick={handlePin}><Icon.Pin size={16} style={{ color: activeThread?.pinned ? 'var(--primary)' : undefined }} /></button>
            </div>

            <div ref={scrollRef} style={{ flex: 1, overflow: 'auto' }}>
              <div style={{ maxWidth: 760, margin: '24px auto', padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
                {loadingMsgs && <div style={{ color: 'var(--text-4)', fontSize: 12.5 }}>Cargando mensajes…</div>}
                {!loadingMsgs && messages.length === 0 && (
                  <div style={{ color: 'var(--text-4)', fontSize: 13, textAlign: 'center', padding: '30px 0' }}>
                    Empieza la conversación. Por ejemplo: <em>“Resume los criterios de adjudicación”</em>.
                  </div>
                )}
                {messages.map((m) => <FullChatMessage key={m.id} message={m} currentUser={CURRENT_USER} />)}
                {sending && <TypingBubble />}
              </div>
            </div>

            <div style={{ padding: '14px 22px', borderTop: '1px solid var(--line-1)' }}>
              <div style={{ maxWidth: 760, margin: '0 auto' }}>
                <div style={{ display: 'flex', gap: 7, marginBottom: 10, flexWrap: 'wrap' }}>
                  {['Resume los criterios de adjudicación', 'Calcula el margen estimado', '¿Qué documentos del Sobre 1 faltan?', '¿Qué riesgos críticos hay?'].map((s) => (
                    <button key={s} className="chip" style={{ cursor: 'pointer', fontSize: 12 }} onClick={() => setDraft(s)} disabled={sending}><Icon.Spark size={12} /> {s}</button>
                  ))}
                </div>
                <ChatComposer
                  inputRef={inputRef}
                  value={draft} onChange={setDraft} onSubmit={send}
                  disabled={sending}
                  placeholder="Pregúntale a Lizzi sobre esta licitación…"
                >
                  <button className="btn btn-primary btn-icon" onClick={send} disabled={!draft.trim() || sending} title="Enviar" style={{ width: 38, height: 38, borderRadius: 10 }}>
                    {sending ? <span className="spin" style={{ display: 'inline-flex' }}><Icon.Sparkles size={16} /></span> : <Icon.Send size={16} />}
                  </button>
                </ChatComposer>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 11, color: 'var(--text-4)' }}>
                  <span>⏎ enviar · ⇧⏎ nueva línea</span>
                  <span>Memoria activa: automática</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Panel de contexto */}
      <div className={`chat-ctx${showCtx ? ' is-open' : ''}`} style={{ borderLeft: '1px solid var(--line-1)', background: 'var(--bg-1)', overflow: 'auto' }}>
        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span className="page-eyebrow" style={{ margin: 0 }}>Contexto</span>
            <button className="btn btn-ghost btn-icon chat-ctx-toggle" title="Cerrar" onClick={() => setShowCtx(false)}><Icon.X size={14} /></button>
          </div>
          {activeBid ? (
            <div className="card card-pad" style={{ marginTop: 10, background: 'var(--bg-2)' }}>
              <div className="font-mono" style={{ fontSize: 10.5, color: 'var(--text-4)', letterSpacing: '0.05em' }}>{activeBid.code || ''} {activeBid.lot ? `· ${activeBid.lot}` : ''}</div>
              <a href="#" onClick={(e) => { e.preventDefault(); goToBid(activeBid.id); }} title={activeBid.title}
                style={{ display: 'block', fontSize: 13, fontWeight: 500, marginTop: 4, color: 'var(--text-1)', textDecoration: 'none' }}>
                {bidTitle(activeBid)} <Icon.ArrowUpRight size={12} style={{ color: 'var(--primary)', verticalAlign: '-1px' }} />
              </a>
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                {activeBid.budgetBase > 0 && <span className="chip"><Icon.Coins size={11} /> {fmtMoney(activeBid.budgetBase)}</span>}
                {activeBid.deadline && <span className="chip"><Icon.Calendar size={11} /> en {activeBid.deadlineDays} d</span>}
                <StatusChip status={activeBid.status} />
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-4)' }}>Esta conversación no está asociada a ninguna licitación.</div>
          )}
        </div>

        {activeBid && (
          <div style={{ padding: '0 18px 16px' }}>
            <div className="section-title" style={{ marginTop: 14, marginBottom: 8 }}>Fuentes activas</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <SourceRow tone="sem-violet" name="Ficha de análisis (IIL)" status={activeBid.analysisStatus === 'done' ? 'Generada' : (activeBid.analysisStatus === 'processing' ? 'En curso' : 'Pendiente')} />
              {docs.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-4)', padding: '2px 4px' }}>Sin pliegos subidos.</div>}
              {docs.map((d) => (
                <SourceRow key={d.id} tone={d.extract_status === 'done' ? 'sem-info' : 'sem-warning'} name={d.file_name} status={d.extract_status === 'done' ? 'Leído' : d.extract_status} />
              ))}
            </div>

            <div className="section-title" style={{ marginTop: 18, marginBottom: 8 }}>Memoria activa</div>
            {memory.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-4)' }}>Lizzi irá guardando aquí los hechos clave.</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {memory.slice(0, 8).map((m) => (
                <div key={m.id} style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.5, padding: '8px 10px', border: '1px solid var(--line-2)', borderRadius: 8, background: 'var(--bg-2)' }}>
                  <span className="chip" style={{ fontSize: 10, marginBottom: 4 }}>{m.kind}</span>
                  <div>{m.content}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

    </div>
      {picker && <ThreadPicker bids={BIDS} onPick={handleNewThread} onClose={() => setPicker(false)} />}
    </div>
  );
}

function SourceRow({ tone, name, status }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 8, alignItems: 'center', padding: '6px 4px' }}>
      <span className={`sem ${tone}`} style={{ width: 6, height: 6, padding: 0, borderRadius: 99 }}></span>
      <span style={{ fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
      <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{status}</span>
    </div>
  );
}

function ThreadPicker({ bids, onPick, onClose }) {
  const [q, setQ] = useState('');
  const list = bids.filter((b) => !q.trim() || `${b.title || ''} ${b.shortTitle || ''}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 80 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 460, maxWidth: '92vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--line-1)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <LizziAvatar size={32} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Nueva conversación</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Elige la licitación que dará contexto a Lizzi</div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><Icon.X size={15} /></button>
        </div>
        <div style={{ padding: '12px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', border: '1px solid var(--line-2)', borderRadius: 8, background: 'var(--bg-2)' }}>
            <Icon.Search size={13} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar licitación…" style={{ background: 'transparent', border: 0, fontSize: 13, flex: 1, color: 'var(--text-1)', outline: 'none' }} />
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '0 12px 12px' }}>
          {list.length === 0 && <div style={{ padding: 16, fontSize: 12.5, color: 'var(--text-4)' }}>No hay licitaciones. Crea una primero.</div>}
          {list.map((b) => (
            <div key={b.id} onClick={() => onPick(b)} style={{ padding: '10px 10px', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={b.title}>{bidTitle(b)}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>{b.entityShort || b.entity || ''}</div>
              </div>
              <StatusChip status={b.status} />
            </div>
          ))}
        </div>
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line-1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>O empieza sin contexto</span>
          <button className="btn btn-outline" style={{ fontSize: 12.5 }} onClick={() => onPick(null)}>Conversación general</button>
        </div>
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div style={{ display: 'flex', gap: 14 }}>
      <LizziAvatar size={36} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, height: 32 }}>
        {[0, 1, 2].map((i) => (
          <span key={i} style={{ width: 6, height: 6, borderRadius: 99, background: 'var(--text-4)', animation: 'dotFlash 1.2s infinite', animationDelay: `${i * 0.18}s` }} />
        ))}
      </div>
    </div>
  );
}

function ThreadRow({ thread, bids, active, onClick, onDelete }) {
  const bid = bids.find((b) => b.id === thread.bid_id);
  const updated = thread.updated_at ? new Date(thread.updated_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '';
  return (
    <div onClick={onClick} className="thread-row" style={{
      padding: '11px 11px', borderRadius: 10, cursor: 'pointer',
      background: active ? 'var(--bg-active)' : 'transparent',
      borderLeft: active ? '2px solid var(--primary)' : '2px solid transparent',
      display: 'flex', flexDirection: 'column', gap: 4, position: 'relative',
    }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'var(--bg-hover)'; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        {thread.pinned && <Icon.Pin size={13} style={{ color: 'var(--primary)', flexShrink: 0 }} />}
        <span style={{ fontSize: 13, fontWeight: active ? 600 : 500, color: active ? 'var(--text-1)' : 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{thread.title}</span>
        <button className="btn btn-ghost btn-icon thread-del" title="Eliminar" onClick={(e) => { e.stopPropagation(); onDelete(); }} style={{ width: 26, height: 26 }}><Icon.Trash size={14} /></button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--text-4)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Icon.Briefcase size={11} style={{ flexShrink: 0 }} />{bid?.entityShort || bid?.entity || 'General'}
        </span>
        <span style={{ flexShrink: 0 }}>{updated}</span>
      </div>
    </div>
  );
}

function FullChatMessage({ message, currentUser }) {
  const isUser = message.role === 'user';
  const at = message.created_at ? new Date(message.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <div style={{ display: 'flex', gap: 14 }}>
      {isUser
        ? <Avatar user={currentUser} size={36} />
        : <LizziAvatar size={36} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600 }}>{isUser ? (currentUser?.name || 'Tú') : 'Lizzi'}</span>
          {at && <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{at}</span>}
        </div>
        <div style={{
          fontSize: 14, lineHeight: 1.65, color: 'var(--text-1)',
          ...(isUser ? { background: 'var(--bg-2)', border: '1px solid var(--line-1)', borderRadius: 12, padding: '10px 14px', display: 'inline-block' } : {}),
        }}>
          <FormattedText text={message.content} />
        </div>
        {message.blocks?.map((b, i) => <ChatBlock key={i} block={b} />)}
        {!isUser && (
          <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
            <button className="btn btn-ghost" style={{ fontSize: 11.5, padding: '4px 8px' }} onClick={() => { navigator.clipboard?.writeText(message.content || ''); notify('Copiado al portapapeles', 'Check'); }}><Icon.Copy size={12} /> Copiar</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ===============================================
// DEMO — comportamiento simulado (modo /demo)
// ===============================================
function DemoChat() {
  const { CHAT } = window.MockData;
  const [thread, setThread] = useState(CHAT.threads[0] || null);
  const [messages, setMessages] = useState(CHAT.messages);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages]);

  const send = () => {
    if (!draft.trim() || streaming) return;
    const at = new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    setMessages((m) => [...m, { role: 'user', content: draft.trim(), at }]);
    const prompt = draft.trim();
    setDraft(''); setStreaming(true);
    const reply = prompt.toLowerCase().includes('compar')
      ? 'Comparo los dos pliegos por estructura, importe y criterios de adjudicación. La diferencia principal es el peso del Sobre 3: SANDETEL puntúa 50% por fórmula vs 45% en Andalucía Emprende, así que en SANDETEL el precio pesa más.'
      : 'Tomo nota. Esta es una respuesta simulada del chat con Lizzi (modo demo). En la app real envío tu petición a Gemini con el contexto completo de la licitación.';
    setMessages((m) => [...m, { role: 'lizzi', content: '', at, streaming: true }]);
    let i = 0;
    const tick = () => {
      i += Math.max(2, Math.round(Math.random() * 6));
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: reply.slice(0, i) }; return c; });
      if (i < reply.length) setTimeout(tick, 24);
      else { setStreaming(false); setMessages((m) => { const c = [...m]; c[c.length - 1].streaming = false; return c; }); }
    };
    setTimeout(tick, 220);
  };

  return (
    <div className="chat-wrap fade-in">
    <div className="chat-layout chat-layout-2">
      <div className="chat-threads" style={{ borderRight: '1px solid var(--line-1)', background: 'var(--bg-1)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '14px 16px' }}>
          <span className="page-eyebrow" style={{ margin: 0 }}>Conversaciones · demo</span>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '4px 8px 10px' }}>
          {CHAT.threads.map((t) => (
            <div key={t.id} onClick={() => setThread(t)} style={{ padding: '10px', borderRadius: 8, cursor: 'pointer', background: thread?.id === t.id ? 'var(--bg-active)' : 'transparent' }}>
              <span style={{ fontSize: 12.5 }}>{t.title}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-0)' }}>
        <div style={{ padding: '14px 22px', borderBottom: '1px solid var(--line-1)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <LizziAvatar size={40} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{thread?.title || 'Nueva conversación'}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Modo demostración · respuestas simuladas</div>
          </div>
        </div>
        <div ref={scrollRef} style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ maxWidth: 760, margin: '24px auto', padding: '0 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 14 }}>
                {m.role === 'user'
                  ? <Avatar user={window.MockData.CURRENT_USER} size={36} />
                  : <LizziAvatar size={36} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{m.role === 'user' ? (window.MockData.CURRENT_USER?.name || 'Tú') : 'Lizzi'}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{m.at}</span>
                  </div>
                  <div className={m.streaming ? 'cursor-blink' : ''} style={{ fontSize: 14, lineHeight: 1.65, color: 'var(--text-1)' }}>
                    <FormattedText text={m.content} />
                  </div>
                  {m.blocks?.map((b, j) => <ChatBlock key={j} block={b} />)}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--line-1)' }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <ChatComposer
              value={draft} onChange={setDraft} onSubmit={send}
              disabled={streaming}
              placeholder="Escribe un mensaje (demo)…"
            >
              <button className="btn btn-primary btn-icon" onClick={send} disabled={!draft.trim() || streaming} title="Enviar" style={{ width: 38, height: 38, borderRadius: 10 }}><Icon.Send size={16} /></button>
            </ChatComposer>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}

window.ChatScreen = ChatScreen;

export { ChatScreen, ThreadRow, FullChatMessage };
